import React from 'react';
import PropTypes from 'prop-types';
import CmsInlineEdit from '../../../components/Cms/CmsInlineEdit';
import Link from '../../../components/LinkWithCountry/LinkWithCountry';
import {
  GIFTING_FAQ_CONTACT_TAB_PATH,
  GIFTING_FAQ_SEE_ALL_LABEL,
} from '../giftingFaqConstants';

const faqAccordionAddIcon = require('../imgs/faq-accordion-add.svg');
const faqAccordionCloseIcon = require('../imgs/faq-accordion-close.svg');

const renderSectionHeading = (title) => {
  if (!title) {
    return null;
  }
  const commaIndex = title.indexOf(',');
  if (commaIndex === -1) {
    return title;
  }
  return (
    <>
      {title.slice(0, commaIndex + 1)}
      <br />
      {title.slice(commaIndex + 1).trim()}
    </>
  );
};

const FaqSection = ({ faqSection, expandedQuestionId, onToggleFaq }) => {
  if (!faqSection?.faqQuestions?.length) {
    return null;
  }

  const { title, faqQuestions, cmsNode: deliveryCategoryNode } = faqSection;

  return (
    <div
      className="mx-auto flex w-full max-w-[900px] flex-col items-center gap-8"
      data-testid="gifting-faq-section"
    >
      {title && (
      <h2
        className="text-center font-brandRefreshGrauna uppercase text-brandRefresh-surface-onSurface text-[36px] leading-[40px] md:text-[60px] md:leading-[60px]"
      >
        {renderSectionHeading(title)}
      </h2>
        )}

      <div className="flex w-full flex-col items-center gap-10">
        <CmsInlineEdit type="list" cmsNode={deliveryCategoryNode} showContent showChilds>
          <ul className="flex w-full flex-col gap-3 list-none p-0 m-0 md:gap-4">
            {faqQuestions.map((question, index) => {
                const faqId = question.cmsKeyId ?? index;
                const isOpen = expandedQuestionId === faqId;
                const answerNode = question.answerCmsNode;

                return (
                  <li
                    key={faqId}
                    className={`overflow-hidden rounded-xl transition-colors duration-300 md:rounded-2xl ${
                      isOpen
                        ? 'bg-brandRefresh-accent'
                        : 'border border-brandRefresh-surface-onSurface bg-transparent'
                    }`}
                  >
                    {isOpen ? (
                      <div className="flex flex-col gap-2.5 px-4 pt-3 pb-4 md:gap-4 md:px-6 md:pt-6 md:pb-7">
                        <div className="flex w-full items-start justify-between gap-3 md:gap-4">
                          <span
                            className="flex-1 text-left font-brandRefreshHenrietta text-base leading-5 text-brandRefresh-background-primary md:text-2xl md:leading-5"
                          >
                            <CmsInlineEdit cmsNode={question.cmsNode}>
                              {question.question}
                            </CmsInlineEdit>
                          </span>
                          <button
                            type="button"
                            className="flex-shrink-0 h-5 w-5 p-0 border-0 bg-transparent focus:outline-none focus-visible:ring-2 focus-visible:ring-brandRefresh-background-primary md:h-6 md:w-6"
                            onClick={() => onToggleFaq(faqId)}
                            aria-label="Close question"
                            aria-expanded
                          >
                            <img
                              src={faqAccordionCloseIcon}
                              alt=""
                              className="h-5 w-5 object-contain md:h-6 md:w-6"
                            />
                          </button>
                        </div>
                        {answerNode && (
                          <div className="font-brandRefreshFellix text-sm font-semibold leading-5 text-brandRefresh-background-primary md:text-base md:leading-6 [&_a]:underline">
                            <CmsInlineEdit cmsNode={answerNode}>
                              {answerNode.value ?? question.answer}
                            </CmsInlineEdit>
                          </div>
                        )}
                      </div>
                    ) : (
                      <div
                        className="flex cursor-pointer items-center justify-between gap-3 py-3 pl-4 pr-3 md:gap-4 md:px-6 md:py-6"
                        onClick={() => onToggleFaq(faqId)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            e.preventDefault();
                            onToggleFaq(faqId);
                          }
                        }}
                        role="button"
                        tabIndex={0}
                        aria-expanded={false}
                      >
                        <span
                          className="flex-1 pr-2 text-left font-brandRefreshHenrietta text-base leading-5 text-brandRefresh-surface-onSurface md:text-2xl md:leading-5"
                        >
                          <CmsInlineEdit cmsNode={question.cmsNode}>
                            {question.question}
                          </CmsInlineEdit>
                        </span>
                        <img
                          src={faqAccordionAddIcon}
                          alt=""
                          className="h-5 w-5 flex-shrink-0 object-contain md:h-6 md:w-6"
                        />
                      </div>
                    )}
                  </li>
                );
              })}
          </ul>
        </CmsInlineEdit>

        <Link
          to={GIFTING_FAQ_CONTACT_TAB_PATH}
          className="text-center font-brandRefreshHenrietta text-base leading-[14px] text-brandRefresh-surface-onSurface underline hover:opacity-90 focus-visible:outline focus-visible:outline-2 focus-visible:outline-brandRefresh-surface-onSurface focus-visible:outline-offset-2 md:text-lg md:leading-[14px]"
        >
          {GIFTING_FAQ_SEE_ALL_LABEL}
        </Link>
      </div>
    </div>
  );
};

FaqSection.propTypes = {
  faqSection: PropTypes.shape({
    title: PropTypes.string,
    faqQuestions: PropTypes.arrayOf(PropTypes.shape({
      question: PropTypes.string,
      answer: PropTypes.string,
      cmsKeyId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
      cmsNode: PropTypes.shape({}),
      answerCmsNode: PropTypes.shape({}),
    })),
    cmsNode: PropTypes.shape({}),
  }),
  expandedQuestionId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onToggleFaq: PropTypes.func.isRequired,
};

FaqSection.defaultProps = {
  faqSection: null,
  expandedQuestionId: null,
};

export default FaqSection;
